import { Box, Button, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import type { RegistryDocument } from '../../data/types';
import DocumentViewerDialog from '../../documents/DocumentViewerDialog';
import { date, DEMO_TODAY, money, unitPrice, units as formatUnits } from '../../lib/format';
import { sumBy, useInvestorScope } from '../../store/selectors';
import { KeyValueGrid } from '../../ui/primitives';
import { Card, InvestorTitle, SectionLabel, shortEntityName } from '../common';
import TransactionList from '../transactions/TransactionList';

export default function HoldingDetailPage() {
  const { holderId, classId } = useParams();
  const scope = useInvestorScope();
  const [viewing, setViewing] = useState<RegistryDocument | null>(null);
  const holding = scope.holdings.find(
    (item) => item.holderId === holderId && item.classId === classId,
  );
  if (!holding) return <Navigate to="/investor/portfolio" replace />;
  const fund = scope.funds.find((item) => item.id === holding.fundId)!;
  const unitClass = scope.classes.find((item) => item.id === holding.classId)!;
  const holder = scope.holders.find((item) => item.id === holding.holderId)!;
  const entries = scope.ledger.filter(
    (entry) => entry.holderId === holderId && entry.classId === classId,
  );
  const income = sumBy(
    entries.filter((entry) => entry.type === 'DISTRIBUTION'),
    (entry) => entry.amount,
  );
  const documents = scope.documents.filter(
    (doc) => doc.holderId === holderId && (doc.fundId === fund.id || !doc.fundId),
  );
  const canRedeem = fund.basis === 'NAV_UNITS';

  return (
    <>
      <InvestorTitle
        title={fund.name}
        subtitle={`${unitClass.name} · ${shortEntityName(holder)} · account ${holder.accountNumber}`}
        action={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() =>
                setViewing({
                  id: `hs-${holder.id}`,
                  holderId: holder.id,
                  kind: 'HOLDING_STATEMENT',
                  name: 'Holding statement (today)',
                  date: DEMO_TODAY,
                  sizeKb: 0,
                  visibleToInvestor: true,
                })
              }
            >
              Holding statement
            </Button>
            <Button
              component={RouterLink}
              to={`/investor/requests?holder=${holder.id}&class=${unitClass.id}`}
              variant="contained"
            >
              {canRedeem ? 'Redeem or transfer' : 'Transfer'}
            </Button>
          </Stack>
        }
      />
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        }}
      >
        <Card>
          <Typography variant="caption" color="text.secondary">
            Value
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '1.3rem' }}>
            {money(holding.value)}
          </Typography>
        </Card>
        <Card>
          <Typography variant="caption" color="text.secondary">
            Units
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '1.3rem' }}>
            {formatUnits(holding.units)}
          </Typography>
        </Card>
        <Card>
          <Typography variant="caption" color="text.secondary">
            Unit price
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '1.3rem' }}>
            {unitPrice(unitClass.unitPrice)}
          </Typography>
        </Card>
        <Card>
          <Typography variant="caption" color="text.secondary">
            Income received
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '1.3rem' }}>{money(income)}</Typography>
        </Card>
      </Box>
      <SectionLabel>Details</SectionLabel>
      <Card>
        <KeyValueGrid
          columns={3}
          items={[
            { label: 'Fund strategy', value: fund.strategy },
            { label: 'Target return', value: `${unitClass.targetReturnPct}% p.a.` },
            {
              label: 'Distributions',
              value: fund.distributionFrequency.toLowerCase().replace('_', '-'),
            },
            { label: 'Holder since', value: date(holding.firstDate) },
            { label: 'Cost base', value: money(holding.costBase) },
            ...(holding.commitment !== undefined
              ? [
                  { label: 'Commitment', value: money(holding.commitment) },
                  { label: 'Uncalled', value: money(holding.uncalled ?? 0) },
                ]
              : [
                  {
                    label: 'Maturity',
                    value: fund.maturityDate ? date(fund.maturityDate) : 'Open-ended',
                  },
                ]),
            {
              label: 'Reinvestment',
              value: canRedeem
                ? holder.drpElection
                  ? 'Reinvesting distributions'
                  : 'Paid to bank'
                : 'Paid to bank',
            },
          ]}
        />
      </Card>
      <SectionLabel>Transactions</SectionLabel>
      <TransactionList entries={entries} />
      <SectionLabel>Documents for this holding</SectionLabel>
      <Card sx={{ p: 0 }}>
        {documents.slice(0, 8).map((doc, index) => (
          <Stack
            key={doc.id}
            direction="row"
            justifyContent="space-between"
            sx={{
              px: 2,
              py: 1.25,
              borderTop: index ? '1px solid' : 'none',
              borderColor: 'divider',
              cursor: 'pointer',
            }}
            onClick={() => setViewing(doc)}
          >
            <Typography variant="body2">{doc.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {date(doc.date)}
            </Typography>
          </Stack>
        ))}
      </Card>
      <DocumentViewerDialog doc={viewing} onClose={() => setViewing(null)} />
    </>
  );
}
